import { supabase } from "@/integrations/supabase/client";
import { getAIApiUrl } from "./apiRoutes";

interface GenerateVehicleImageParams {
  vehicleId: string;
  make: string;
  model: string;
  year?: number;
  nickname?: string;
  color?: string;
}

/**
 * Generates an AI image for a vehicle and uploads it to the vehicle-images bucket.
 * Fire-and-forget: updates the vehicle record with the image URL on success.
 * Does not throw on failure — silently logs errors.
 */
export async function generateAndUploadVehicleImage({
  vehicleId,
  make,
  model,
  year,
  nickname,
  color,
}: GenerateVehicleImageParams): Promise<void> {
  try {
    // Skip if we have no useful info to generate from
    const hasMakeModel = make && make !== "Unknown" && model && model !== "Unknown";
    if (!hasMakeModel && !nickname) {
      console.log("Skipping vehicle image generation: insufficient info");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const url = getAIApiUrl("generate-vehicle-image");
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ make, model, year, nickname, color }),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      console.warn("Vehicle image generation failed:", errData.error || response.status);
      return;
    }

    const { imageDataUrl } = await response.json();
    if (!imageDataUrl) return;

    // Convert base64 data URL to a Blob
    const base64Match = imageDataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!base64Match) {
      console.warn("Invalid image data URL format");
      return;
    }

    const mimeType = base64Match[1];
    const base64Data = base64Match[2];
    const binaryData = atob(base64Data);
    const bytes = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      bytes[i] = binaryData.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType });

    // Upload to vehicle-images bucket (use unique name to bust cache)
    const ext = mimeType.includes("png") ? "png" : "jpg";
    const timestamp = Date.now();
    const fileName = `${session.user.id}/${vehicleId}-ai-generated-${timestamp}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("vehicle-images")
      .upload(fileName, blob, { upsert: true });

    if (uploadError) {
      console.warn("Failed to upload generated vehicle image:", uploadError.message);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("vehicle-images")
      .getPublicUrl(fileName);

    // Update the vehicle record with the image URL
    const { error: updateError } = await supabase
      .from("vehicles")
      .update({ image_url: publicUrl })
      .eq("id", vehicleId);

    if (updateError) {
      console.warn("Failed to update vehicle with generated image:", updateError.message);
    }
  } catch (err) {
    console.warn("Vehicle image generation error (non-blocking):", err);
  }
}
